import { Component, Input, OnDestroy } from '@angular/core';
import { WebsiteModel } from 'src/app/model/website/website.model';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { environmentCommon } from 'src/environments/environment-common';

// Platform logos by platform name, from the same website registry the theme
// pages use, so a caption written as the system names it ("JetBrains",
// "Sublime Text", ...) gets that platform's own logo.
const PLATFORM_LOGOS: { [ name: string ]: string } = Object.values(
  environmentCommon.website as { [ key: string ]: WebsiteModel },
).reduce( ( map, w ) => {
  if ( w && w.name && w.logo && !map[ w.name ] ) {
    map[ w.name ] = w.logo;
  }
  return map;
}, {} as { [ name: string ]: string } );

export interface DeckShot {
  src: string;
  alt: string;
  caption: string;
}

// Blog widget: the theme's screenshots as a stack of cards. The front card is the
// one in view, the ones behind it peek out above it. Next (or a click on the front
// card) fades the front card out and drops it to the back while the others move
// up; Previous brings the last card to the front, where it fades in. The shots come
// from the post itself: the markdown wraps a plain <figure> list in
// <!--SHOTS-START-->...<!--SHOTS-END--> (that list stays in the feeds, the .md
// copies and the crawler body), and BlogPostPageComponent reads it into `shots`.
// Each caption is the platform's name as the website registry writes it, and the
// platform's logo is shown beside it. Used by every Anatomy post.
@Component( {
  selector: 'app-screenshot-deck',
  templateUrl: './screenshot-deck.component.html',
  styleUrls: [ './screenshot-deck.component.scss' ],
} )
export class ScreenshotDeckComponent implements OnDestroy {

  // How long the front card takes to fade out before it drops to the back. Keep in
  // step with the .sd-fading transition in the stylesheet.
  private static readonly FADE_MS = 280;

  @Input() shots: DeckShot[] = [];

  // Index of the card in front, and of the card that is faded out (if any).
  front = 0;
  fading: number | null = null;

  private timer: any = null;

  constructor( private analyticsService: AnalyticsService ) {
  }

  // Place of card i in the stack: 0 is the front, 1 right behind it, and so on.
  logo( caption: string ): string | null {
    return PLATFORM_LOGOS[ caption ] || null;
  }

  pos( i: number ): number {
    const n = this.shots.length;
    return ( ( i - this.front ) % n + n ) % n;
  }

  next(): void {
    this.step( 1 );
  }

  prev(): void {
    this.step( -1 );
  }

  show( i: number ): void {
    if ( i === this.front || this.fading !== null ) {
      return;
    }
    this.front = i;
    this.track();
  }

  ngOnDestroy(): void {
    if ( this.timer ) {
      clearTimeout( this.timer );
    }
  }

  // Next: the front card fades out, then (still invisible) jumps to the back.
  // Previous: the last card (still invisible) jumps to the front, then fades in.
  private step( dir: 1 | -1 ): void {
    const n = this.shots.length;
    if ( n < 2 || this.fading !== null ) {
      return;
    }
    const target = ( this.front + dir + n ) % n;
    if ( this.reducedMotion() ) {
      this.front = target;
      this.track();
      return;
    }
    if ( dir === 1 ) {
      this.fading = this.front;
      this.later( ScreenshotDeckComponent.FADE_MS, () => {
        this.front = target;
        this.track();
        this.later( 40, () => this.fading = null );
      } );
    } else {
      this.fading = target;
      this.front = target;
      this.track();
      this.later( 40, () => this.fading = null );
    }
  }

  private later( ms: number, fn: () => void ): void {
    this.timer = setTimeout( () => {
      this.timer = null;
      fn();
    }, ms );
  }

  private track(): void {
    this.analyticsService.logEvent( 'Screenshot Deck Changed', { platform: this.shots[ this.front ]?.caption } );
  }

  private reducedMotion(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
  }
}
